/**
 * ConditionDropZone — the WHEN clause editor for a step.
 *
 * Supports TWO-LEVEL boolean logic: one or more condition GROUPS combined by
 * the step's `groupLogic`, where each group combines its own rows by the
 * group's `logic`. This lets the user express things like
 *   (Approved with conditions AND Outstanding conditions open) OR (CD signed)
 *
 * Each group is its own drop target — releasing a field from the palette over
 * a group appends a comparison row to THAT group. The id format is
 * `cond-zone:<stepKey>:<groupId>` so the parent (VisualBuilder) can route the
 * dropped field to the right group.
 */

import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import type { VisualConditionGroup, VisualConditionRow } from './serialize.js';
import { ConditionRow } from './ConditionRow.js';

export const CONDITION_DROP_PREFIX = 'cond-zone:';

export function makeConditionZoneId(stepKey: string, groupId: string): string {
  return `${CONDITION_DROP_PREFIX}${stepKey}:${groupId}`;
}

/** Parse a condition zone id back into its (stepKey, groupId). */
export function parseConditionZoneId(
  zoneId: string,
): { stepKey: string; groupId: string } | null {
  if (!zoneId.startsWith(CONDITION_DROP_PREFIX)) return null;
  const rest = zoneId.slice(CONDITION_DROP_PREFIX.length);
  const sep = rest.indexOf(':');
  if (sep < 0) return null;
  return { stepKey: rest.slice(0, sep), groupId: rest.slice(sep + 1) };
}

export interface ConditionDropZoneProps {
  stepKey: string;
  groupLogic: 'and' | 'or';
  groups: readonly VisualConditionGroup[];
  readOnly: boolean;
  unsupported: boolean;
  onChangeGroupLogic: (logic: 'and' | 'or') => void;
  onChangeGroupInnerLogic: (groupId: string, logic: 'and' | 'or') => void;
  onChangeRow: (groupId: string, rowId: string, next: VisualConditionRow) => void;
  onRemoveRow: (groupId: string, rowId: string) => void;
  onAddGroup: () => void;
  onRemoveGroup: (groupId: string) => void;
}

export function ConditionDropZone({
  stepKey,
  groupLogic,
  groups,
  readOnly,
  unsupported,
  onChangeGroupLogic,
  onChangeGroupInnerLogic,
  onChangeRow,
  onRemoveRow,
  onAddGroup,
  onRemoveGroup,
}: ConditionDropZoneProps) {
  if (unsupported) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        This step uses a deeply nested condition that the visual builder
        can&apos;t render yet. Open the <strong>Structured form</strong> tab to
        edit the guard, or remove the guard here to start over.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {groups.map((group, idx) => (
        <div key={group.id}>
          {idx > 0 && (
            <div className="my-1.5 flex items-center justify-center gap-2">
              <span className="h-px flex-1 bg-slate-200" />
              <LogicToggle
                value={groupLogic}
                readOnly={readOnly}
                onChange={onChangeGroupLogic}
                size="lg"
              />
              <span className="h-px flex-1 bg-slate-200" />
            </div>
          )}
          <ConditionGroupBox
            stepKey={stepKey}
            group={group}
            groupIndex={idx}
            showRemove={groups.length > 1}
            readOnly={readOnly}
            onChangeInnerLogic={(logic) => onChangeGroupInnerLogic(group.id, logic)}
            onChangeRow={(rowId, next) => onChangeRow(group.id, rowId, next)}
            onRemoveRow={(rowId) => onRemoveRow(group.id, rowId)}
            onRemove={() => onRemoveGroup(group.id)}
          />
        </div>
      ))}

      {!readOnly && (
        <button
          type="button"
          onClick={onAddGroup}
          className="mt-1 w-full rounded-lg border border-dashed border-slate-300 py-1.5 text-[11px] font-medium text-slate-500 hover:border-brand-navy-300 hover:text-brand-navy-700"
        >
          + Add condition group{' '}
          <span className="text-slate-400">
            (combined with {groupLogic.toUpperCase()})
          </span>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A single group box — its own drop target.
// ---------------------------------------------------------------------------

function ConditionGroupBox({
  stepKey,
  group,
  groupIndex,
  showRemove,
  readOnly,
  onChangeInnerLogic,
  onChangeRow,
  onRemoveRow,
  onRemove,
}: {
  stepKey: string;
  group: VisualConditionGroup;
  groupIndex: number;
  showRemove: boolean;
  readOnly: boolean;
  onChangeInnerLogic: (logic: 'and' | 'or') => void;
  onChangeRow: (rowId: string, next: VisualConditionRow) => void;
  onRemoveRow: (rowId: string) => void;
  onRemove: () => void;
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: makeConditionZoneId(stepKey, group.id),
    data: { kind: 'cond-zone', stepKey, groupId: group.id },
  });

  const empty = group.rows.length === 0;

  return (
    <div
      ref={setNodeRef}
      aria-label={`Condition group ${groupIndex + 1}`}
      className={clsx(
        'space-y-2 rounded-xl border-2 p-3 transition-colors',
        isOver
          ? 'border-brand-gold-500 bg-brand-gold-50/60'
          : empty
            ? 'border-dashed border-slate-300 bg-slate-50'
            : 'border-slate-200 bg-white',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
          Group {groupIndex + 1}
        </span>
        {group.rows.length > 1 && (
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="font-medium text-slate-500">match</span>
            <LogicToggle value={group.logic} readOnly={readOnly} onChange={onChangeInnerLogic} />
          </div>
        )}
        {showRemove && !readOnly && (
          <button
            type="button"
            onClick={onRemove}
            title="Remove this group"
            className="ml-auto rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            aria-label={`Remove group ${groupIndex + 1}`}
          >
            ✕
          </button>
        )}
      </div>

      {group.rows.map((row) => (
        <ConditionRow
          key={row.id}
          row={row}
          readOnly={readOnly}
          onChange={(next) => onChangeRow(row.id, next)}
          onRemove={() => onRemoveRow(row.id)}
        />
      ))}

      {empty ? (
        <p className="px-2 py-2 text-center text-xs text-slate-500">
          Drag a <span className="font-medium text-brand-navy-700">field</span> here.
        </p>
      ) : (
        <p className="pt-0.5 text-right text-[10px] uppercase tracking-wider text-slate-400">
          Drop another field to add a row
        </p>
      )}
    </div>
  );
}

function LogicToggle({
  value,
  readOnly,
  onChange,
  size = 'sm',
}: {
  value: 'and' | 'or';
  readOnly: boolean;
  onChange: (logic: 'and' | 'or') => void;
  size?: 'sm' | 'lg';
}) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
      {(['and', 'or'] as const).map((op) => (
        <button
          key={op}
          type="button"
          disabled={readOnly}
          onClick={() => onChange(op)}
          className={clsx(
            'rounded font-semibold uppercase tracking-wider transition-colors',
            size === 'lg' ? 'px-3 py-1 text-[11px]' : 'px-2 py-0.5 text-[10px]',
            value === op
              ? 'bg-brand-navy-700 text-white'
              : 'text-slate-500 hover:bg-slate-50',
          )}
        >
          {op}
        </button>
      ))}
    </div>
  );
}
